import FormattedMessage from 'I18n/components/FormattedMessage';
import { MOVE_DOWN, MOVE_UP, REMOVE } from './types';
import { INVALID_FILE_VALUE } from '../validation/PropertyValidationErrorCodes';
import { buildInvalidValidationState, buildSaveableValidationState } from '../validation/hooks/usePropertyValidation';
import { DISALLOWED_EXTENSION, MAX_FILE_UPLOAD_SIZE_FREE_IN_MB, MAX_FILE_UPLOAD_SIZE_PAID_IN_MB, MAX_UPLOAD_SIZE_EXCEEDED, TIMEOUT } from './constants';
import { formatMultiEnumValue, parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { splitNameAndExtension } from 'file-manager-components/utils/file';
import { HIDDEN_IN_APP_PRIVATE_NOT_INDEXABLE, HIDDEN_IN_APP_PUBLIC_TO_ALL_NOT_INDEXABLE, HIDDEN_IN_APP_SENSITIVE } from 'file-manager-components/constants/fileAccess';
import { jsx as _jsx } from "react/jsx-runtime";
export const getFileExtensionFromReference = fileReference => {
  if (!fileReference || !fileReference.label) {
    return '';
  }
  return splitNameAndExtension(fileReference.label).extension.toLowerCase();
};

// picking up relevant types from
// https://git.hubteam.com/HubSpot/FileManager/blob/master/FileManagerCore/src/main/java/com/hubspot/content/filemanager/core/FileManagerErrorType.java
// we don't have to keep them in sync because we are going to show custom messages only for a handful of them
export const getFileUploadErrorMessage = (errorType, hasNoUploadLimit) => {
  switch (errorType) {
    case DISALLOWED_EXTENSION:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputFilesV2.fileUploadErrors.disallowedExtension"
      });
    case MAX_UPLOAD_SIZE_EXCEEDED:
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "customerDataProperties.PropertyInputFilesV2.fileUploadErrors.maxUploadSizeExceeded",
        options: {
          maxFileSize: hasNoUploadLimit ? MAX_FILE_UPLOAD_SIZE_PAID_IN_MB : MAX_FILE_UPLOAD_SIZE_FREE_IN_MB
        }
      });
    case TIMEOUT:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputFilesV2.fileUploadErrors.timeout"
      });
    default:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputFilesV2.fileUploadErrors.unknownError"
      });
  }
};
export const getValidationState = (errorsMap, property, value) => {
  if (errorsMap.size !== 0) {
    return buildInvalidValidationState(property, value, [{
      // there could be multiple uploads in error state. Hence we are using standard error code and message here
      code: INVALID_FILE_VALUE,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputFilesV2.invalidFileValue"
      })
    }]);
  }
  return buildSaveableValidationState(property, value);
};
export const isAboveMaxSizeLimit = (file, hasNoUploadLimit) => {
  const maxSizeInMB = hasNoUploadLimit ? MAX_FILE_UPLOAD_SIZE_PAID_IN_MB : MAX_FILE_UPLOAD_SIZE_FREE_IN_MB;
  return file.size > maxSizeInMB * 1024 * 1024;
};
export const getFilesBelowMaxSizeLimit = (files, hasNoUploadLimit) => files.filter(file => !isAboveMaxSizeLimit(file, hasNoUploadLimit));
export const getFilesAboveMaxSizeLimit = (files, hasNoUploadLimit) => files.filter(file => isAboveMaxSizeLimit(file, hasNoUploadLimit));
export const getMissingReferences = (fileReferences, fileIds) => {
  if (!fileReferences) {
    return fileIds;
  }
  return fileIds.filter(fileId => !fileReferences.some(fileReference => fileReference.id === fileId));
};

// this function picks references based on fileIds
// reorders references per fileIds and helps on race conditions
export const getFileReferencesByIds = (fileReferences, fileIds) => {
  if (!fileReferences || fileIds.length === 0) {
    return [];
  }
  const filteredFileReferences = [];
  fileIds.forEach(id => {
    const reference = fileReferences.find(r => r.id === id);
    if (reference) {
      filteredFileReferences.push(reference);
    }
  });
  return filteredFileReferences;
};
export const computeProgress = e =>
// There is a time delay between the progress reaching to 100 and the API returning file information.
// We are stopping the progress bar at 97% to avoid a void state (files uploaded to 100 but UI showing no changes)
e.lengthComputable ? Math.round(e.loaded / e.total * 97) : 0;
export const removeTrailingSemiColons = value => {
  if (!value || !value.includes(';')) {
    return value;
  }
  return value.replace(/;+$/, '');
};
export const addTrailingSemiColons = (value, errorsMap) => {
  if (errorsMap.size === 0) {
    return value;
  }
  const errorString = Array(errorsMap.size).fill(';').join('');
  return value ? value + errorString : errorString;
};
const swap = (aIndex, bIndex, arr) => {
  if (aIndex < 0 || bIndex < 0 || aIndex >= arr.length || bIndex >= arr.length) {
    return;
  }
  [arr[aIndex], arr[bIndex]] = [arr[bIndex], arr[aIndex]];
};
const remove = (index, arr) => {
  if (index < 0 || index >= arr.length) {
    return;
  }
  arr.splice(index, 1);
};
export const getNewValue = ({
  currentValue,
  missingReferences,
  action,
  fileId
}) => {
  if (!currentValue) {
    return '';
  }
  if (!currentValue.includes(fileId)) {
    return currentValue;
  }

  // for up and down moves we need to skip missing reference ids in the value
  // For example, if value = '123;456;unknown;789' and if we are moving 789 up
  // then the value should be '123;789;456;unknown'. We need to skip unresolved file ids
  const filteredValues = parseMultiEnumValue(currentValue).filter(id => id && !missingReferences.includes(id));
  if (action === MOVE_UP) {
    const index = filteredValues.indexOf(fileId);
    swap(index, index - 1, filteredValues);
    return formatMultiEnumValue([...filteredValues, ...missingReferences]);
  }
  if (action === MOVE_DOWN) {
    const index = filteredValues.indexOf(fileId);
    swap(index, index + 1, filteredValues);
    return formatMultiEnumValue([...filteredValues, ...missingReferences]);
  }
  if (action === REMOVE) {
    const splitValues = parseMultiEnumValue(currentValue);
    const index = splitValues.indexOf(fileId);
    remove(index, splitValues);
    return formatMultiEnumValue(splitValues);
  }
  return currentValue;
};
export const getFileVisibilityForProperty = (propertyExtensionsMetadata, propertyName) => {
  var _propertyExtensionsMe;
  if (propertyExtensionsMetadata && Object.prototype.hasOwnProperty.call(propertyExtensionsMetadata, propertyName) && ((_propertyExtensionsMe = propertyExtensionsMetadata[propertyName]) === null || _propertyExtensionsMe === void 0 || (_propertyExtensionsMe = _propertyExtensionsMe.metadata) === null || _propertyExtensionsMe === void 0 || (_propertyExtensionsMe = _propertyExtensionsMe.fileVisibility) === null || _propertyExtensionsMe === void 0 ? void 0 : _propertyExtensionsMe.value) === 'public') {
    return 'public';
  }
  return 'private';
};
export const getFileAccess = (fileVisibility, dataSensitivity) => {
  // default to most restrictive and loosen based on configuration
  let fileAccess = HIDDEN_IN_APP_SENSITIVE;
  if (dataSensitivity === 'none') {
    fileAccess = HIDDEN_IN_APP_PRIVATE_NOT_INDEXABLE;
    if (fileVisibility === 'public') {
      fileAccess = HIDDEN_IN_APP_PUBLIC_TO_ALL_NOT_INDEXABLE;
    }
  }
  return fileAccess;
};